import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import InvoiceView from '../components/billing/InvoiceView';
import CancelRetentionModal from '../components/billing/CancelRetentionModal';
import {
  getBillingStatus, getBillingPlans, getBillingPayments, billingCheckout, billingCancel,
  getBillingRequisites, saveBillingRequisites, createBillingInvoice, getBillingInvoices,
  applyRetentionOffer,
} from '../services/api';
import { formatDate, formatMoney } from '../utils/formatters';
import '../components/common/ui.css';
import './BillingPage.css';

const STATUS_LABELS = { trial: 'Триал', active: 'Активна', suspended: 'Приостановлена' };
const PAYMENT_STATUS_LABELS = { pending: 'В обработке', succeeded: 'Оплачено', canceled: 'Отклонено' };
const INVOICE_STATUS_LABELS = { pending: 'Ожидает оплаты', paid: 'Оплачен', canceled: 'Отменён' };
const PERIOD_LABELS = { month: 'мес.', year: 'год' };

// ✓ / ✗ есть ли фича в тарифе — вместо эмодзи-глифов.
const StatusIcon = ({ ok }) => (
  ok ? (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.5L10.5 14.5L15.5 9.5" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" d="M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5" />
    </svg>
  )
);
const ENTITY_TYPE_LABELS = { ip: 'ИП', ooo: 'ООО' };

const EMPTY_REQUISITES = {
  entity_type: 'ip', company_name: '', inn: '', kpp: '', ogrn: '', legal_address: '',
  bank_name: '', bik: '', checking_account: '', correspondent_account: '', signatory: '', contact_email: '',
};

// После редиректа с ЮKassa платёж может ещё не обработаться вебхуком —
// коротко подождём и перепроверим статус пару раз.
const POLL_DELAYS_MS = [1500, 3000, 5000];

const BillingPage = () => {
  const { user, isAuthenticated, isPlatformAdmin, loading: authLoading, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkingOut, setCheckingOut] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const [requisites, setRequisites] = useState(null);
  const [platformRequisites, setPlatformRequisites] = useState(null);
  const [requisitesForm, setRequisitesForm] = useState(EMPTY_REQUISITES);
  const [savingRequisites, setSavingRequisites] = useState(false);
  const [requisitesError, setRequisitesError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [periodDefaulted, setPeriodDefaulted] = useState(false);
  const [showCancelRetention, setShowCancelRetention] = useState(false);
  const RETENTION_DISCOUNT_PERCENT = 20;

  const load = useCallback(async () => {
    try {
      const [s, p, h, r, inv] = await Promise.all([
        getBillingStatus(), getBillingPlans(), getBillingPayments(),
        getBillingRequisites(), getBillingInvoices(),
      ]);
      setStatus(s);
      setPlans(p.plans || []);
      setPayments(h.payments || []);
      setPlatformRequisites(r.platform_requisites || null);
      if (r.requisites) {
        setRequisites(r.requisites);
        setRequisitesForm({ ...EMPTY_REQUISITES, ...r.requisites });
      }
      setInvoices(inv.invoices || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить данные биллинга');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Годовой тариф выбран по умолчанию только для первой покупки (триал/нет
  // текущего тарифа) — уже активную организацию, которая продлевает или
  // меняет тариф, дефолтом не трогаем. periodDefaulted — чтобы сработало
  // ровно один раз, а не при каждом повторном load() (например, после отмены).
  useEffect(() => {
    if (periodDefaulted || !status || plans.length === 0) return;
    if (status.status !== 'active' && plans.some((p) => p.billing_period === 'year')) {
      setPeriodFilter('year');
    }
    setPeriodDefaulted(true);
  }, [status, plans, periodDefaulted]);

  // Вернулись с оплаты — план ещё мог не смениться (ждём вебхук)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('payment')) return;
    window.history.replaceState({}, '', '/billing');
    POLL_DELAYS_MS.forEach((delay) => setTimeout(load, delay));
  }, [load]);

  if (!isAuthenticated) return null;
  if (!authLoading && (isPlatformAdmin || (user && user.role !== 'admin'))) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleCheckout = async (plan) => {
    const isSwitch = status.status === 'active' && status.plan && status.plan.id !== plan.id;
    if (isSwitch && !window.confirm(
      `Сменить тариф на «${plan.name}»? Спишется полная стоимость нового тарифа сейчас, доступ к новому тарифу применится сразу.`
    )) return;

    setCheckingOut(plan.id);
    setError('');
    try {
      const data = await billingCheckout(plan.id, promoCode.trim());
      window.location.href = data.confirmation_url;
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось начать оплату');
      setCheckingOut(null);
    }
  };

  const handleCreateInvoice = async (plan) => {
    if (!requisites) {
      setError('Сначала укажите реквизиты организации ниже');
      return;
    }
    setCreatingInvoice(plan.id);
    setError('');
    try {
      const data = await createBillingInvoice(plan.id, promoCode.trim());
      setInvoices((prev) => [data.invoice, ...prev]);
      setViewInvoice(data.invoice);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось выставить счёт');
    } finally {
      setCreatingInvoice(null);
    }
  };

  const handleSaveRequisites = async (e) => {
    e.preventDefault();
    setSavingRequisites(true);
    setRequisitesError('');
    try {
      const data = await saveBillingRequisites(requisitesForm);
      setRequisites(data.requisites);
    } catch (err) {
      setRequisitesError(err.response?.data?.error || 'Не удалось сохранить реквизиты');
    } finally {
      setSavingRequisites(false);
    }
  };

  const requisitesField = (key) => ({
    value: requisitesForm[key] || '',
    onChange: (e) => setRequisitesForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const handleStay = async () => {
    setError('');
    try {
      await applyRetentionOffer();
      await load();
      setShowCancelRetention(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось применить скидку');
    }
  };

  const handleDowngrade = () => {
    // Тарифы уже видны на этой же странице ниже — просто закрываем модалку,
    // никакого отдельного экрана выбора не нужно.
    setShowCancelRetention(false);
  };

  const handleConfirmCancel = async () => {
    setCanceling(true);
    setError('');
    try {
      await billingCancel();
      await load();
      setShowCancelRetention(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось отменить подписку');
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="billing-page">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      {loading && <LoadingSpinner />}
      {!loading && error && <div className="ui-inline-error">{error}</div>}

      {!loading && status && (
        <div className="billing-body">
          <section className="ui-card">
            <div className="ui-card-head">
              <h2>Подписка</h2>
            </div>
            <dl className="ui-defs">
              <div>
                <dt>Статус</dt>
                <dd>
                  <span className={`ui-status ${status.status === 'suspended' ? 'off' : 'on'}`}>
                    {status.status === 'active' && status.cancel_requested
                      ? 'Активна до конца периода'
                      : (STATUS_LABELS[status.status] || status.status)}
                  </span>
                </dd>
              </div>
              {status.status === 'trial' && status.trial_ends_at && (
                <div>
                  <dt>Пробный период</dt>
                  <dd>до {formatDate(status.trial_ends_at)}</dd>
                </div>
              )}
              <div>
                <dt>Текущий тариф</dt>
                <dd>{status.plan ? `${status.plan.name} — ${status.plan.price} ₽/${PERIOD_LABELS[status.plan.billing_period] || status.plan.billing_period}` : 'не выбран'}</dd>
              </div>
              <div>
                <dt>Способ оплаты</dt>
                <dd>{status.payment_method_title || 'не сохранён'}</dd>
              </div>
              <div>
                <dt>{status.cancel_requested ? 'Доступ сохранён до' : 'Следующее списание'}</dt>
                <dd>{formatDate(status.next_charge_at)}</dd>
              </div>
            </dl>
            {!status.configured && (
              <div className="ui-inline-error" style={{ marginTop: 'var(--s-4)' }}>
                Приём платежей ещё не настроен на платформе.
              </div>
            )}
            {status.plan?.overage_price_per_seat && status.plan?.max_employees
              && status.employees_count > status.plan.max_employees && (
              <div className="ui-dim" style={{ marginTop: 'var(--s-4)' }}>
                У вас {status.employees_count - status.plan.max_employees} мест сверх лимита тарифа —
                доплата {(status.employees_count - status.plan.max_employees) * status.plan.overage_price_per_seat} ₽
                добавится к следующему списанию.
              </div>
            )}
            {status.status === 'active' && status.cancel_requested && (
              <div className="ui-inline-error" style={{ marginTop: 'var(--s-4)' }}>
                Подписка отменена: автопродление выключено, доступ сохранится до {status.next_charge_at ? formatDate(status.next_charge_at) : 'конца периода'}.
              </div>
            )}
            {status.status === 'active' && !status.cancel_requested && status.payment_method_title && (
              <div className="ui-form-actions" style={{ marginTop: 'var(--s-4)' }}>
                <button type="button" className="ui-btn-danger" onClick={() => setShowCancelRetention(true)} disabled={canceling}>
                  {canceling ? 'Отменяем…' : 'Отменить подписку'}
                </button>
              </div>
            )}
          </section>

          <section className="ui-card">
            <div className="ui-card-head">
              <h2>Тарифы</h2>
              <p>Оплата картой через ЮKassa (автопродление) или по счёту на реквизиты организации.</p>
            </div>
            <div className="ui-field billing-promo-field">
              <label htmlFor="promo-code">Промокод (необязательно)</label>
              <input
                id="promo-code" type="text" value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Например, SAVE20"
              />
            </div>
            {plans.some((p) => p.billing_period === 'year') && (
              <div className="ui-tabs billing-period-tabs">
                <button type="button" className={`ui-tab ${periodFilter === 'month' ? 'active' : ''}`} onClick={() => setPeriodFilter('month')}>
                  Помесячно
                </button>
                <button type="button" className={`ui-tab ${periodFilter === 'year' ? 'active' : ''}`} onClick={() => setPeriodFilter('year')}>
                  На год
                </button>
              </div>
            )}
            {plans.length === 0 ? (
              <div className="ui-empty">Тарифы пока не заведены платформой</div>
            ) : plans.filter((p) => p.billing_period === periodFilter).length === 0 ? (
              <div className="ui-empty">Годовых тарифов пока нет — уточните у платформы</div>
            ) : (
              <div className="billing-plans">
                {plans.filter((p) => p.billing_period === periodFilter).map((plan) => {
                  const isCurrent = status.plan?.id === plan.id;
                  const isSwitch = status.status === 'active' && status.plan && !isCurrent;
                  const monthlyTwin = plan.billing_period === 'year'
                    ? plans.find((p) => p.name === plan.name && p.billing_period === 'month')
                    : null;
                  const savingsPercent = monthlyTwin && monthlyTwin.price > 0
                    ? Math.round((1 - plan.price / (monthlyTwin.price * 12)) * 100)
                    : null;
                  return (
                    <div className={`billing-plan ${isCurrent ? 'current' : ''}`} key={plan.id}>
                      <div className="billing-plan-name-row">
                        <div className="billing-plan-name">{plan.name}</div>
                        {savingsPercent > 0 && <span className="ui-chip billing-plan-savings">-{savingsPercent}%</span>}
                      </div>
                      <div className="billing-plan-price">
                        {plan.price} ₽<span> / {PERIOD_LABELS[plan.billing_period] || plan.billing_period}</span>
                      </div>
                      {plan.description && <div className="ui-dim">{plan.description}</div>}
                      <ul className="billing-plan-features">
                        <li>{plan.max_employees ? `До ${plan.max_employees} сотрудников` : 'Без лимита сотрудников'}</li>
                        <li className={plan.has_crm ? '' : 'off'} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><StatusIcon ok={plan.has_crm} /> CRM и гостевая база</li>
                        <li className={plan.has_kpi ? '' : 'off'} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><StatusIcon ok={plan.has_kpi} /> KPI-аналитика</li>
                      </ul>
                      <button
                        type="button"
                        className="ui-btn-primary"
                        disabled={!status.configured || checkingOut === plan.id}
                        onClick={() => handleCheckout(plan)}
                      >
                        {checkingOut === plan.id ? 'Переходим к оплате…' : isCurrent ? 'Продлить картой' : isSwitch ? 'Сменить тариф (карта)' : 'Оплатить картой'}
                      </button>
                      <button
                        type="button"
                        className="ui-btn-ghost"
                        disabled={creatingInvoice === plan.id}
                        onClick={() => handleCreateInvoice(plan)}
                        title={!requisites ? 'Сначала укажите реквизиты организации ниже' : ''}
                      >
                        {creatingInvoice === plan.id ? 'Выставляем счёт…' : 'Оплатить по счёту'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="ui-card">
            <div className="ui-card-head">
              <h2>История платежей</h2>
            </div>
            {payments.length === 0 ? (
              <div className="ui-empty">Платежей ещё не было</div>
            ) : (
              <div className="billing-history">
                {payments.map((p) => (
                  <div className="billing-history-row" key={p.id}>
                    <span>{p.description || '—'}</span>
                    <span>{p.currency === 'RUB' ? formatMoney(p.amount) : `${p.amount} ${p.currency}`}</span>
                    <span className={`ui-status ${p.status === 'succeeded' ? 'on' : p.status === 'canceled' ? 'off' : ''}`}>
                      {PAYMENT_STATUS_LABELS[p.status] || p.status}
                    </span>
                    <span className="ui-dim">{p.created_at ? formatDate(p.created_at) : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="ui-card">
            <div className="ui-card-head">
              <h2>Счета на оплату</h2>
              <p>Оплата по счёту подтверждается платформой вручную после поступления средств.</p>
            </div>
            {invoices.length === 0 ? (
              <div className="ui-empty">Счетов ещё не было</div>
            ) : (
              <div className="billing-history">
                {invoices.map((inv) => (
                  <div className="billing-history-row" key={inv.id}>
                    <span>Счёт № {inv.number}</span>
                    <span>{inv.currency === 'RUB' ? formatMoney(inv.amount) : `${inv.amount} ${inv.currency}`}</span>
                    <span className={`ui-status ${inv.status === 'paid' ? 'on' : inv.status === 'canceled' ? 'off' : ''}`}>
                      {INVOICE_STATUS_LABELS[inv.status] || inv.status}
                    </span>
                    <button type="button" className="ui-btn-ghost" onClick={() => setViewInvoice(inv)}>Показать</button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="ui-card">
            <div className="ui-card-head">
              <h2>Реквизиты организации</h2>
              <p>Нужны для выставления счёта на оплату по безналу.</p>
            </div>
            <form className="ui-form" onSubmit={handleSaveRequisites}>
              {requisitesError && <div className="ui-inline-error">{requisitesError}</div>}
              <div className="ui-grid">
                <div className="ui-field">
                  <label htmlFor="req-entity-type">Тип организации</label>
                  <select
                    id="req-entity-type"
                    value={requisitesForm.entity_type}
                    onChange={(e) => setRequisitesForm((prev) => ({ ...prev, entity_type: e.target.value }))}
                  >
                    <option value="ip">{ENTITY_TYPE_LABELS.ip}</option>
                    <option value="ooo">{ENTITY_TYPE_LABELS.ooo}</option>
                  </select>
                </div>
                <div className="ui-field">
                  <label htmlFor="req-company-name">Название организации</label>
                  <input id="req-company-name" type="text" required placeholder="Без «ИП»/«ООО» — форма организации выбрана выше" {...requisitesField('company_name')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-inn">ИНН</label>
                  <input id="req-inn" type="text" required {...requisitesField('inn')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-kpp">КПП</label>
                  <input id="req-kpp" type="text" {...requisitesField('kpp')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-ogrn">ОГРН / ОГРНИП</label>
                  <input id="req-ogrn" type="text" {...requisitesField('ogrn')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-contact-email">Email для связи</label>
                  <input id="req-contact-email" type="email" {...requisitesField('contact_email')} />
                </div>
                <div className="ui-field wide">
                  <label htmlFor="req-legal-address">Юридический адрес</label>
                  <input id="req-legal-address" type="text" {...requisitesField('legal_address')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-bank-name">Банк</label>
                  <input id="req-bank-name" type="text" {...requisitesField('bank_name')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-bik">БИК</label>
                  <input id="req-bik" type="text" {...requisitesField('bik')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-checking-account">Расчётный счёт</label>
                  <input id="req-checking-account" type="text" {...requisitesField('checking_account')} />
                </div>
                <div className="ui-field">
                  <label htmlFor="req-correspondent-account">Корр. счёт</label>
                  <input id="req-correspondent-account" type="text" {...requisitesField('correspondent_account')} />
                </div>
                <div className="ui-field wide">
                  <label htmlFor="req-signatory">Подписант (ФИО, должность)</label>
                  <input id="req-signatory" type="text" {...requisitesField('signatory')} />
                </div>
              </div>
              <div className="ui-form-actions">
                <button type="submit" className="ui-btn-primary" disabled={savingRequisites}>
                  {savingRequisites ? 'Сохраняем…' : 'Сохранить реквизиты'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {viewInvoice && (
        <InvoiceView
          invoice={viewInvoice}
          platformRequisites={platformRequisites}
          plan={plans.find((p) => p.id === viewInvoice.plan_id)}
          onClose={() => setViewInvoice(null)}
        />
      )}

      {showCancelRetention && (
        <CancelRetentionModal
          discountPercent={RETENTION_DISCOUNT_PERCENT}
          onStay={handleStay}
          onDowngrade={handleDowngrade}
          onCancel={handleConfirmCancel}
          onClose={() => setShowCancelRetention(false)}
        />
      )}
    </div>
  );
};

export default BillingPage;
